import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { LocationCredential } from '@/app/session/startup-session';
import { LocationBootstrapClient } from '@/features/location-bootstrap/location-bootstrap-client';
import { LocationBootstrapRepository } from '@/features/location-bootstrap/location-bootstrap-repository';
import type { LocationBootstrapPayload } from '@/features/location-bootstrap/location-bootstrap-types';
import { fieldDatabase } from '@/shared/db/default-field-database';
import { messages } from './messages';

interface LocationBootstrapContextValue {
  bootstrap: LocationBootstrapPayload | null;
  error: string | null;
  isLoading: boolean;
  lastLoadedAt: string | null;
  refresh: () => Promise<LocationBootstrapPayload | null>;
}

const LocationBootstrapContext = createContext<LocationBootstrapContextValue | null>(null);

export function LocationBootstrapProvider({
  activeLocation,
  children,
}: {
  activeLocation: LocationCredential | null;
  children: ReactNode;
}) {
  const intl = useIntl();
  const client = useMemo(() => new LocationBootstrapClient(), []);
  const repository = useMemo(() => new LocationBootstrapRepository(fieldDatabase), []);
  const [state, setState] = useState<{
    bootstrap: LocationBootstrapPayload | null;
    error: string | null;
    isLoading: boolean;
  }>({
    bootstrap: null,
    error: null,
    isLoading: false,
  });

  const refresh = useCallback(async () => {
    if (!activeLocation?.locationId) {
      setState({
        bootstrap: null,
        error: activeLocation ? intl.formatMessage(messages.bootstrapMissingBackendId) : null,
        isLoading: false,
      });
      return null;
    }

    setState(current => ({ ...current, error: null, isLoading: true }));

    try {
      const payload = await client.load({
        locationApiKey: activeLocation.apiKey,
        locationId: activeLocation.locationId,
        refreshCache: true,
      });
      await repository.save({ apiKey: activeLocation.apiKey, payload });
      setState({ bootstrap: payload, error: null, isLoading: false });

      return payload;
    } catch (error) {
      const cached = await repository.get({
        apiKey: activeLocation.apiKey,
        locationId: activeLocation.locationId,
      });
      const cachedPayload = cached ? toPayload(cached) : null;
      setState({
        bootstrap: cachedPayload,
        error: error instanceof Error ? error.message : intl.formatMessage(messages.bootstrapRefreshError),
        isLoading: false,
      });

      return cachedPayload;
    }
  }, [activeLocation, client, repository, intl.formatMessage]);

  useEffect(() => {
    if (!activeLocation?.locationId) {
      setState({ bootstrap: null, error: null, isLoading: false });
      return;
    }

    let isCancelled = false;

    async function loadBootstrap() {
      setState(current => ({ ...current, error: null, isLoading: true }));

      const cached = await repository.get({
        apiKey: activeLocation?.apiKey ?? '',
        locationId: activeLocation?.locationId ?? '',
      });
      const cachedPayload = cached ? toPayload(cached) : null;

      if (!isCancelled && cachedPayload) {
        setState({ bootstrap: cachedPayload, error: null, isLoading: false });
      }

      if (globalThis.navigator?.onLine === false) {
        if (!(isCancelled || cachedPayload)) {
          setState({
            bootstrap: null,
            error: intl.formatMessage(messages.bootstrapNoCachedOffline),
            isLoading: false,
          });
        }
        return;
      }

      try {
        const apiKey = activeLocation?.apiKey ?? '';
        const payload = await client.load({
          locationApiKey: apiKey,
          locationId: activeLocation?.locationId ?? '',
        });
        await repository.save({ apiKey, payload });

        if (!isCancelled) {
          setState({ bootstrap: payload, error: null, isLoading: false });
        }
      } catch (error) {
        if (!isCancelled) {
          setState({
            bootstrap: cachedPayload,
            error: error instanceof Error ? error.message : intl.formatMessage(messages.bootstrapLoadError),
            isLoading: false,
          });
        }
      }
    }

    void loadBootstrap();

    return () => {
      isCancelled = true;
    };
  }, [activeLocation, client, repository, intl.formatMessage]);

  const value = useMemo<LocationBootstrapContextValue>(
    () => ({
      bootstrap: state.bootstrap,
      error: state.error,
      isLoading: state.isLoading,
      lastLoadedAt: state.bootstrap?.loadedAt ?? null,
      refresh,
    }),
    [refresh, state],
  );

  return <LocationBootstrapContext.Provider value={value}>{children}</LocationBootstrapContext.Provider>;
}

export function useLocationBootstrap() {
  const value = useContext(LocationBootstrapContext);

  if (!value) {
    throw new Error('useLocationBootstrap must be used inside LocationBootstrapProvider.');
  }

  return value;
}

function toPayload(record: Awaited<ReturnType<LocationBootstrapRepository['get']>>): LocationBootstrapPayload | null {
  if (!record) {
    return null;
  }

  return {
    buildings: record.data.buildings as LocationBootstrapPayload['buildings'],
    existingMeters: record.data.existingMeters as LocationBootstrapPayload['existingMeters'],
    loadedAt: record.loadedAt,
    location: record.data.location as LocationBootstrapPayload['location'],
    locationId: record.locationId,
    manufacturers: record.data.manufacturers as LocationBootstrapPayload['manufacturers'],
    meterModels: record.data.meterModels as LocationBootstrapPayload['meterModels'],
    ownerSuggestions: record.data.ownerSuggestions ?? [],
    tenants: record.data.tenants,
    thirdParties: (record.data.thirdParties ?? []) as LocationBootstrapPayload['thirdParties'],
  };
}
